import { isRewardFareClass, type FareClassWithReward } from 'constants/flight'
import type { CharterRewardLegResult } from 'hooks/Flights/useCharterRewardAvailability'
import { getEffectiveFlightPrice, isFnfPriceActive } from 'lib/flights/flightUtils'
import { getSeatsLeftFromJourney, hasSufficientSeats } from '../hooks/utils'

export interface MaldivesCharterTripOptionsTableCellPrice {
  /** Hotel + flight total in the local currency for the column's fare class. */
  cashTotal: number | undefined
  /**
   * Hotel member price + flight total when a hotel member price exists for the date.
   * Cells render this with `colour="lux-plus"` when LuxPlus is enabled.
   */
  cashMemberTotal?: number
  /** Per-cabin points required across both legs (reward columns only). */
  pointsTotal?: number
  /** Per-cabin cash co-pay across both legs (reward columns only). */
  cashCoPayTotal?: number
  /**
   * Reward column only: every leg has reward inventory but the minimum is below the
   * requested seat-eligible passenger count. The cell should render the
   * "Not enough seats available for your selection" copy instead of plain
   * "No seats available".
   */
  rewardSeatsInsufficient?: boolean
  /** True when the cash price uses the Friends & Family discounted fare. */
  isFnf?: boolean
  /** True when the cash price uses a flight promotion fare. */
  isPromotion?: boolean
  /** Departing journey promotional seat inventory for sale messaging. */
  promotionSeatsLeft?: number
}

interface ComputeFareClassCellPriceArgs {
  fareClass: FareClassWithReward
  hotelPrice: number | undefined
  /** Hotel member price (LuxPlus) for the date — fareClass-independent. */
  hotelMemberPrice?: number
  /**
   * Cabin-specific outbound journey for this row's `checkIn`. The shell fetches
   * `useAllCharterFlightJourneys` once per selectable cabin and the calling
   * cell looks up the journey for `toBaseFareClass(fareClassColumn)` from context — so the
   * `price.all.totalFare` here is already correct for this column's cabin.
   */
  departingFlight: App.JourneyV2 | undefined
  returningFlight: App.JourneyV2 | undefined
  seatEligiblePassengers: number
  getLegResult: (journeyKey: string) => CharterRewardLegResult | undefined
}

/**
 * Cell-level pricing for a single (date row, fare-class column) intersection in the
 * Maldives charter trip options table.
 *
 * Cash columns: hotel + cabin-specific journey total fares (caller passes per-cabin journeys
 * sourced from the per-cabin shell fetch — no upsell-fare lookup needed).
 *
 * Reward columns: the shell fetches all 3 cabins' journeys and includes all journey_keys
 * in the availability request, so `getLegResult(journeyKey)` resolves for any cabin.
 */
export function computeFareClassCellPrice(
  args: ComputeFareClassCellPriceArgs,
): MaldivesCharterTripOptionsTableCellPrice {
  const { fareClass, hotelPrice, hotelMemberPrice, departingFlight, returningFlight, seatEligiblePassengers, getLegResult } = args

  // Cash cabin total: each cabin's journey is resolved by the caller (per-cabin fetch), so
  // no upsell lookup is needed. We route through `getEffectiveFlightPrice` so each cabin's
  // own FNF eligibility (`journey.fnfEligible` + `fnfSeatsLeft`) decides whether to use
  // the regular fare or the FNF discounted fare — independent of what cabin the page has
  // selected. Result: a cabin with FNF inventory always shows its FNF price, a cabin
  // without FNF always shows its regular price, and the values stay stable as the user
  // toggles cabins in the page-level filter.
  const departingFare = departingFlight
    ? getEffectiveFlightPrice(departingFlight, seatEligiblePassengers).all.totalFare
    : undefined
  const returningFare = returningFlight
    ? getEffectiveFlightPrice(returningFlight, seatEligiblePassengers).all.totalFare
    : undefined

  // Cash seat inventory: take the minimum across both legs of the row's date and gate the
  // cash total on `hasSufficientSeats`. Insufficient seats → cell falls through to "Unavailable".
  const departingSeatsLeft = getSeatsLeftFromJourney(departingFlight)
  const returningSeatsLeft = getSeatsLeftFromJourney(returningFlight)
  const cashSeatsLeft =
    departingSeatsLeft !== undefined && returningSeatsLeft !== undefined
      ? Math.min(departingSeatsLeft, returningSeatsLeft)
      : (departingSeatsLeft ?? returningSeatsLeft)
  const cashSeatsSufficient = hasSufficientSeats(cashSeatsLeft, seatEligiblePassengers)

  const flightTotal =
    departingFare !== undefined && returningFare !== undefined
      ? departingFare + returningFare + (departingFlight?.feederEstimatedPrice ?? 0) + (returningFlight?.feederEstimatedPrice ?? 0)
      : undefined

  const cashTotal =
    cashSeatsSufficient && hotelPrice !== undefined && flightTotal !== undefined
      ? hotelPrice + flightTotal
      : undefined

  const cashMemberTotal =
    cashSeatsSufficient && hotelMemberPrice !== undefined && flightTotal !== undefined
      ? hotelMemberPrice + flightTotal
      : undefined

  const isFnf = !!(
    departingFlight && isFnfPriceActive(departingFlight, seatEligiblePassengers) ||
    returningFlight && isFnfPriceActive(returningFlight, seatEligiblePassengers)
  )
  const isPromotion = !isFnf && !!(departingFlight?.promotionPrice || returningFlight?.promotionPrice)
  // Taking max, bcz offer might be available only on one leg.
  const promotionSeatsLeft = Math.max(departingFlight?.flightGroup.flights[0]?.promotionSeatsLeft ?? 0,
    returningFlight?.flightGroup.flights[0]?.promotionSeatsLeft ?? 0)

  if (!isRewardFareClass(fareClass)) {
    return {
      cashTotal,
      cashMemberTotal,
      isFnf: isFnf && cashTotal !== undefined,
      isPromotion: isPromotion && cashTotal !== undefined,
      promotionSeatsLeft: !isFnf && isPromotion && cashTotal !== undefined && promotionSeatsLeft > 0 ? promotionSeatsLeft : undefined,
    }
  }

  // Reward passengers fly on the same physical flight — if the cash flight
  // doesn't have enough seats, reward seats can't be available either.
  if (!cashSeatsSufficient) {
    return { cashTotal: undefined }
  }

  const departingKey = departingFlight?.journeyKey
  const returningKey = returningFlight?.journeyKey
  const departingLeg = departingKey ? getLegResult(departingKey) : undefined
  const returningLeg = returningKey ? getLegResult(returningKey) : undefined

  if (
    !departingLeg?.available ||
    !returningLeg?.available ||
    departingLeg.pointsPrice === undefined ||
    returningLeg.pointsPrice === undefined ||
    departingLeg.cashPrice === undefined ||
    returningLeg.cashPrice === undefined
  ) {
    return { cashTotal }
  }

  // Reward inventory: same pattern as cash but reading `remainingInventory` from the
  // svc-lux-loyalty leg result (the per-leg reward seat count). Insufficient → fall through
  // to "No seats available".
  const departingRewardInventory = departingLeg.remainingInventory
  const returningRewardInventory = returningLeg.remainingInventory
  const rewardSeatsLeft =
    departingRewardInventory !== undefined && returningRewardInventory !== undefined
      ? Math.min(departingRewardInventory, returningRewardInventory)
      : (departingRewardInventory ?? returningRewardInventory)

  if (!hasSufficientSeats(rewardSeatsLeft, seatEligiblePassengers)) {
    return { cashTotal, rewardSeatsInsufficient: true }
  }

  const pointsTotal = (departingLeg.pointsPrice + returningLeg.pointsPrice) * seatEligiblePassengers
  const cashCoPayTotal = (departingLeg.cashPrice + returningLeg.cashPrice) * seatEligiblePassengers
  const rewardCashTotal =
    hotelPrice !== undefined ? hotelPrice + cashCoPayTotal : undefined
  const rewardCashMemberTotal =
    hotelMemberPrice !== undefined ? hotelMemberPrice + cashCoPayTotal : undefined

  return {
    cashTotal: rewardCashTotal,
    cashMemberTotal: rewardCashMemberTotal,
    pointsTotal,
    cashCoPayTotal,
  }
}
